import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Card,
  ConfirmModal,
  OrderLineItem,
  Page,
  RelativeDateTime,
  SimpleSelect,
  SwitchInput,
  useToast,
} from 'corso-admin-ui';
import { isTruthy } from 'corso-types';
import { PropsWithChildren, useState } from 'react';
import { match } from 'ts-pattern';
import api from '~/api';
import DescriptionList from '~/components/DescriptionList';
import { useNavigateSearchData } from '~/components/searchTable/useNavigateSearchData';
import {
  useCanEditShippingClaim,
  useCanRestoreShippingClaim,
} from '~/features/shippingClaimReview/hooks/useCanEditShippingClaim';
import { useShippingClaim } from '~/features/shippingClaimReview/ShippingClaimReviewProvider';
import { useClaimSource } from '~/hooks/useClaimSource';
import { useIsConciergeWidget } from '~/hooks/widgetHooks';
import { useIsWidgetAuth } from '~/providers/AutoAuthProvider';
import { useStoreId } from '~/providers/StoreUserProvider';
import { shippingClaimStatusName } from '~/utils/enumNameMaps';
import { isErrorMessage } from '~/utils/errorResponse';
import {
  useIsFinalizeBlocked,
  useIsReorderGiftCardEnabled,
  useReorderShippingTitles,
} from '../hooks/useShippingClaimLookupConfig';
import { CloseShippingClaim } from './CloseShippingClaim';
import { ManualRefundShippingClaim } from './ManualRefundShippingClaim';
import { ManualReorderShippingClaim } from './ManualReorderShippingClaim';
import { RefundShippingShippingClaim } from './RefundShippingShippingClaim';
import { RestoreShippingClaim } from './RestoreShippingClaim';
import { ShippingClaimReorderTrackingModal } from './ShippingClaimReorderTracking';
import { StoreEmailContacts } from './StoreEmailContacts';

function FinalizeShippingClaim({
  show,
  onClose,
}: {
  show: boolean;
  onClose: () => void;
}) {
  const claim = useShippingClaim();
  const storeId = useStoreId();
  const queryClient = useQueryClient();
  const finalizationSource = useClaimSource();
  const isGiftCardEnabled = useIsReorderGiftCardEnabled();

  const toast = useToast();

  const reorderShippingTitles = useReorderShippingTitles();

  const [useGiftCard, setUseGiftCard] = useState(false);

  // the titles arrive with the lookup, so fall back to the first until one is picked
  const [selectedShippingTitle, setSelectedShippingTitle] = useState<string>();
  const reorderShippingTitle =
    selectedShippingTitle ?? reorderShippingTitles[0];

  const { mutate: finalizeClaim, isPending } = useMutation({
    mutationKey: ['shippingClaimFinalize', claim.id, storeId],
    mutationFn: () =>
      api
        .store(storeId)
        .shippingProtection.claim(`${claim.id}`)
        .finalize({ finalizationSource, useGiftCard, reorderShippingTitle }),
    onSuccess: () => onClose(),
    onError: (error) =>
      toast.show(
        isErrorMessage(error)
          ? error.message
          : 'Could not finalize this claim. Check its timeline and Shopify before trying again.',
      ),
    // a failure lands on the claim's timeline, so refetch either way
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: ['shippingClaim'] });
      // the claims list shows status, and the reorder creates parcels to track
      await queryClient.invalidateQueries({ queryKey: ['shippingClaims'] });
      await queryClient.invalidateQueries({ queryKey: ['orderTracking'] });
    },
  });

  // Global-e reorders always ship with the store's Global-e replacement title
  const { isGlobalE } = claim.originalStoreOrder;

  const showTitleSelection =
    reorderShippingTitles.length > 1 &&
    claim.resolutionMethod === 'Reorder' &&
    !useGiftCard &&
    !isGlobalE;

  const showGiftCardSelection =
    isGiftCardEnabled && claim.resolutionMethod === 'Reorder';

  return (
    <ConfirmModal
      show={show}
      variant="primary"
      title="Finalize Claim"
      prompt={
        <div className="flex flex-col gap-4">
          {(showGiftCardSelection || showTitleSelection) && (
            <Card.Heading>{claim.resolutionMethod} Settings</Card.Heading>
          )}

          {showGiftCardSelection && (
            <SwitchInput
              id="use-gift-card"
              label="Use Gift Card"
              details="Issue a gift card as a replacement instead of a new order."
              checked={useGiftCard}
              onChange={(e) => setUseGiftCard(e.target.checked)}
            />
          )}
          {showTitleSelection && (
            <SimpleSelect
              label="Shipping Title"
              options={reorderShippingTitles.map((title) => ({
                value: title,
                label: title,
              }))}
              value={reorderShippingTitle}
              onChange={setSelectedShippingTitle}
            />
          )}

          <Card.Heading>{claim.resolutionMethod} Summary</Card.Heading>
          <Alert
            variant="info"
            heading={match({
              resolutionMethod: claim.resolutionMethod,
              useGiftCard,
            })
              .with(
                { resolutionMethod: 'Refund' },
                () =>
                  `This will refund claim #${claim.id} against order ${claim.originalStoreOrder.orderNo} to the original payment method.`,
              )
              .with(
                { resolutionMethod: 'Reorder', useGiftCard: true },
                () =>
                  `This will issue the value of claim #${claim.id} as a gift card instead of creating a new order.`,
              )
              .with(
                { resolutionMethod: 'Reorder', useGiftCard: false },
                () => `This will create a new order for claim #${claim.id}.`,
              )
              .exhaustive()}
          />

          <span className="flex flex-col gap-2">
            {claim.shippingClaimLineItems.map((li) => (
              <OrderLineItem
                key={li.id}
                size="sm"
                title={li.name}
                src={li.imgUrl}
                quantity={li.quantity}
                sku={li.sku}
                options={li.optionsFromPlatform}
              />
            ))}
          </span>
          {claim.resolutionMethod === 'Reorder' &&
            !useGiftCard &&
            (isGlobalE || reorderShippingTitle) && (
              <DescriptionList
                descriptions={[
                  {
                    term: 'Shipping Title',
                    details: isGlobalE
                      ? 'Uses the Global-e replacement order title'
                      : reorderShippingTitle,
                  },
                ]}
              />
            )}
        </div>
      }
      confirmText="Finalize Claim"
      loading={isPending}
      onConfirm={() => finalizeClaim()}
      onCancel={onClose}
    />
  );
}

export function ShippingClaimPage({ children }: PropsWithChildren) {
  const shippingClaim = useShippingClaim();
  const isWidgetAuth = useIsWidgetAuth();
  const isCorsoConcierge = useIsConciergeWidget();

  const canEdit = useCanEditShippingClaim();
  const canRestore = useCanRestoreShippingClaim();
  const isFinalizeBlocked = useIsFinalizeBlocked();

  const [showFinalizeModal, setShowFinalizeModal] = useState(false);
  const [showStoreContacts, setShowStoreContacts] = useState(false);
  const [showReorderTracking, setShowReorderTracking] = useState(false);
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [showManualRefundModal, setShowManualRefundModal] = useState(false);
  const [showManualReorderModal, setShowManualReorderModal] = useState(false);
  const [showRefundShippingModal, setShowRefundShippingModal] = useState(false);

  const reorderStoreOrderId =
    shippingClaim.resolutionSummary?.exchangeOrder?.storeOrderId;

  const paginationActions = useNavigateSearchData({
    currentId: shippingClaim.id,
    kind: 'Shipping',
  });

  const moreActions = [
    !!reorderStoreOrderId && {
      id: 'reorder-tracking',
      icon: 'shipment' as const,
      content: 'View Reorder Tracking',
      onAction: () => setShowReorderTracking(true),
    },
    isCorsoConcierge && {
      id: 'store-contacts',
      icon: 'email' as const,
      content: 'View Store Contacts',
      onAction: () => setShowStoreContacts(true),
    },
    canEdit && {
      id: 'manual-refund',
      icon: 'dollar' as const,
      content: 'Manual Refund',
      onAction: () => setShowManualRefundModal(true),
    },
    canEdit && {
      id: 'refund-shipping',
      icon: 'shipment' as const,
      content: 'Refund Shipping',
      onAction: () => setShowRefundShippingModal(true),
    },
    canEdit && {
      id: 'manual-reorder',
      icon: 'order' as const,
      content: 'Manual Reorder',
      onAction: () => setShowManualReorderModal(true),
    },
    canEdit && {
      id: 'close-claim',
      icon: 'close' as const,
      content: 'Close Claim',
      onAction: () => setShowCloseModal(true),
    },
    canRestore && {
      id: 'restore-claim',
      icon: 'undo' as const,
      content: 'Restore Claim',
      onAction: () => setShowRestoreModal(true),
    },
  ].filter(isTruthy);

  return (
    <Page
      backAction={!isWidgetAuth}
      paginationActions={isCorsoConcierge ? undefined : paginationActions}
      title={
        shippingClaim.linkToCustomerUi
          ? {
              content: `#${shippingClaim.id}`,
              to: shippingClaim.linkToCustomerUi,
              target: '_blank',
            }
          : `#${shippingClaim.id}`
      }
      subtitle={
        <>
          <p>{shippingClaimStatusName[shippingClaim.status]}</p>
          <p>
            Created <RelativeDateTime dateTime={shippingClaim.createdOn} full />
          </p>
        </>
      }
      primaryAction={
        !canEdit
          ? undefined
          : {
              content: 'Finalize Claim',
              onAction: () => setShowFinalizeModal(true),
              disabled: isFinalizeBlocked,
            }
      }
      secondaryActions={[
        moreActions.length > 0 && {
          id: 'more-actions',
          content: 'More Actions',
          actions: moreActions,
        },
      ].filter(isTruthy)}
    >
      {canEdit && (
        <FinalizeShippingClaim
          show={showFinalizeModal}
          onClose={() => setShowFinalizeModal(false)}
        />
      )}

      {canEdit && (
        <ManualRefundShippingClaim
          show={showManualRefundModal}
          onClose={() => setShowManualRefundModal(false)}
        />
      )}

      {canEdit && (
        <RefundShippingShippingClaim
          show={showRefundShippingModal}
          onClose={() => setShowRefundShippingModal(false)}
        />
      )}

      {canEdit && (
        <ManualReorderShippingClaim
          show={showManualReorderModal}
          onClose={() => setShowManualReorderModal(false)}
        />
      )}

      {canEdit && (
        <CloseShippingClaim
          show={showCloseModal}
          onClose={() => setShowCloseModal(false)}
        />
      )}

      {canRestore && (
        <RestoreShippingClaim
          show={showRestoreModal}
          onClose={() => setShowRestoreModal(false)}
        />
      )}

      <StoreEmailContacts
        show={showStoreContacts}
        onClose={() => setShowStoreContacts(false)}
      />

      {!!reorderStoreOrderId && (
        <ShippingClaimReorderTrackingModal
          show={showReorderTracking}
          onClose={() => setShowReorderTracking(false)}
          storeOrderId={reorderStoreOrderId}
        />
      )}
      {children}
    </Page>
  );
}
